/* eslint-disable max-classes-per-file */
import { Injectable, inject } from "@angular/core";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { MessageSeverity } from "~app/common/components/message-panel/message-panel.models";
import { Atgardstyp } from "~app/common/models/constants";
import { SearchKontraktResponse } from "~app/common/models/virkeskop/v2/SearchKontraktResponse";
import { VKChecklisteFragaDTO } from "~app/common/models/virkeskop/v2/VKChecklisteFragaDTO";
import { ApiClientService } from "~app/common/services/api-client.service";
import { ISearchVirkeskopAnbudKontraktRequest } from "~app/features/virkeskop/models/kontrakt-search-model";
import { DirektutbyteBerakningRequest, DirektutbyteBerakningResponseDTO, VKDirektutbyteDTO } from "~app/features/virkeskop/models/tradslag/direktutbyte-models";
import { VKSnabbkalkylDTO, VKSnabbkalkylUtbyteDTO, VKSnabbkalkylUtbyteRequestDTO } from "~app/features/virkeskop/models/tradslag/snabbkalkyl-models";
import { IVirkeskopshandelseArbetsorder } from "~app/pages/start/handelser/handelser.models";
import { AtgardstypWithGroupDTO } from "../models/atgardstyp-with-group-dto";

@Injectable({
    providedIn: "root"
})
export class VirkeskopService {
    private readonly http = inject(ApiClientService);

    searchForAnbudKontrakt(params: ISearchVirkeskopAnbudKontraktRequest): Observable<SearchKontraktResponse[]> {
        return this.http.get<SearchKontraktResponse[]>("virkeskopskontrakt/search/", false, params);
    }

    searchSkogsvardFPVAvtal(params: ISearchVirkeskopAnbudKontraktRequest): Observable<SearchKontraktResponse[]> {
        return this.http.get<SearchKontraktResponse[]>("virkeskopskontrakt/searchSkogsvardFPV/", false, params);
    }

    findAnbudKontraktByArbetsorder(beteckning: string): Observable<SearchKontraktResponse[]> {
        return this.http.get<SearchKontraktResponse | null>("virkeskopskontrakt/getbyarbetsorder", false, { beteckning })
            .pipe(map(res => res !== null ? [res] : []));
    }

    makulera(kontraktId: number): Observable<any> {
        return this.http.post(`/Virkeskopskontrakt/${kontraktId}/makulera`, {});
    }

    getVirkeskopErrors(userId: number): Observable<GetVirkeskopAlertsQueryResult[]> {
        return this.http.get<GetVirkeskopAlertsQueryResult[]>(`virkeskoperror/${userId}`, false);
    }

    getAlerts(avtalId: number): Observable<GetVirkeskopAlertsQueryResult[]> {
        return this.http.get<GetVirkeskopAlertsQueryResult[]>(`virkeskoperror/virkeskop/${avtalId}`, false);
    }

    getChecklisteFragor(kontraktGuid: string): Observable<VKChecklisteFragaDTO[]> {
        return this.http.get<VKChecklisteFragaDTO[]>(`virkeskopskontrakt/${kontraktGuid}/checklistefragor`);
    }

    isForyngringsavverkning = (atgardstyp: string): boolean => Atgardstyp.foryngringsavverkningstyper.some(x => x === atgardstyp);

    isForyngringsavverkningOrVagLednAtgardstyp = (atgardstyp: string): boolean => this.isForyngringsavverkning(atgardstyp) ||
        atgardstyp === Atgardstyp.VagLedning;

    isMarkberedningForyngringRecommended = (atgardstyp: string): boolean => [
        Atgardstyp.UtglesningFS,
        Atgardstyp.VagLedning,
        Atgardstyp.VindSkada,
        Atgardstyp.NSFrihuggning,
        Atgardstyp.NSUtglesning,
        Atgardstyp.NSLuckhuggning,
        Atgardstyp.NSRestavv
    ].some(x => x === atgardstyp);

    requiresAvverkningsanmalanAndMaxDistanceForEdit = (atgardstyp: string): boolean =>
        Atgardstyp.foryngringsavverkningstyper.concat([Atgardstyp.VagLedning]).some(x => x === atgardstyp);

    requiresAvverkningsanmalanAndMaxDistance = (atgardstyp: string): boolean => Atgardstyp.foryngringsavverkningstyper.concat([
        Atgardstyp.VagLedning,
        Atgardstyp.VindSkada,
        Atgardstyp.NSFrihuggning,
        Atgardstyp.NSUtglesning,
        Atgardstyp.NSLuckhuggning,
        Atgardstyp.NSRestavv
    ]).some(x => x === atgardstyp);

    getAtgardsTyperWithGroups(): Observable<AtgardstypWithGroupDTO[]> {
        return this.http.get<AtgardstypWithGroupDTO[]>("virkeskopsatgardstyp/getatgardstyperwithgroup", false);
    }

    getSnabbkalkyl(atgardid: number): Observable<VKSnabbkalkylDTO> {
        return this.http.get<VKSnabbkalkylDTO>(`virkeskop2/atgard/${atgardid}/snabbkalkyl`);
    }

    calculateSnabbkalkylUtbyte(request: VKSnabbkalkylUtbyteRequestDTO): Observable<VKSnabbkalkylUtbyteDTO> {
        return this.http.post<any, VKSnabbkalkylUtbyteDTO>(`virkeskop2/atgard/snabbkalkyl/calculate`,
            JSON.stringify(request), false, null, ApiClientService.JsonHeader);
    }

    calculateDirektUtbyte(request: DirektutbyteBerakningRequest): Observable<DirektutbyteBerakningResponseDTO> {
        return this.http.post<any, DirektutbyteBerakningResponseDTO>(`virkeskop2/atgard/direktutbyte/calculate`,
            JSON.stringify(request), false, null, ApiClientService.JsonHeader);
    }

    getDirektutbyte(atgardid: number): Observable<VKDirektutbyteDTO> {
        return this.http.get<VKDirektutbyteDTO>(`virkeskop2/atgard/${atgardid}/direktutbyte`);
    }
}

export class GetVirkeskopAlertsQueryResult {
    virkeskopsavtalId: number;
    namn: string;
    subEntityId?: number;
    subEntityLinkName: string;
    subEntityType: string;
    alerts: Alert[];
    arbetsorderList: IVirkeskopshandelseArbetsorder[];
}

export class Alert {
    title?: string;
    occured?: Date;
    message: string;
    severity: MessageSeverity;
    metadata: { [key: string]: string };
    subEntityLinkName: string;
    subEntityId?: number;
}
